import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import { Refresh, Home } from '@mui/icons-material'
import { recordError, recordEvent } from '../observability/events'

/**
 * Error Boundary para capturar falhas de carregamento de chunks lazy
 *
 * Cenários tratados:
 * - Chunk falhou ao carregar (offline, cache corrompido)
 * - SW desatualizado (nova versão deployada)
 * - Network timeout
 * - Quota exceeded
 *
 * Referência: LAZY_LOADING_OFFLINE_RISKS.md - Solução 1
 */
class LazyErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
    }
  }

  static getDerivedStateFromError(error) {
    // Detectar erro de carregamento de chunk
    const errorMessage = error?.message || ''
    const isChunkError =
      errorMessage.includes('Loading chunk') ||
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('ChunkLoadError') ||
      errorMessage.includes('Loading CSS chunk')

    return {
      hasError: true,
      isChunkError,
      error,
    }
  }

  componentDidCatch(error, errorInfo) {
    console.error('[LazyErrorBoundary] component caught error', error)
    recordError(error, {
      category: 'outro',
      action: 'lazy_load_error_boundary',
      payload: {
        isChunkError: this.state.isChunkError,
        componentStack: errorInfo?.componentStack?.slice(0, 500),
      },
    })

    this.setState({
      errorInfo,
    })
  }

  handleReload = () => {
    recordEvent({
      level: 'info',
      category: 'outro',
      action: 'lazy_load_error_reload_clicked',
      payload: { isChunkError: this.state.isChunkError },
    })

    // Preservar o SW em modo offline: ele é quem fornece o shell e os assets
    // precacheados. A limpeza só é segura quando há rede para baixar a versão.
    if (!navigator.onLine || !('serviceWorker' in navigator)) {
      window.location.reload()
      return
    }

    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        return Promise.all(registrations.map((reg) => reg.unregister()))
      })
      .then(() => {
        window.location.reload()
      })
      .catch((err) => {
        recordError(err, {
          category: 'outro',
          action: 'lazy_load_sw_unregister_failed',
        })
        window.location.reload()
      })
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  handleRetry = () => {
    recordEvent({
      level: 'info',
      category: 'outro',
      action: 'lazy_load_error_retry_clicked',
    })
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
    })
  }

  render() {
    if (this.state.hasError) {
      // Erro de carregamento de chunk (offline, cache corrompido, etc)
      if (this.state.isChunkError) {
        return (
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            minHeight='60vh'
            padding={4}
            style={{ textAlign: 'center' }}
          >
            <Box marginBottom={2}>
              <Typography variant='h4' gutterBottom color='error'>
                Erro ao Carregar Página
              </Typography>
            </Box>
            <Box maxWidth={600} marginBottom={3}>
              <Typography variant='body1' color='textSecondary' paragraph>
                Não foi possível carregar esta página. Isso pode acontecer
                quando:
              </Typography>

              <Box textAlign='left' marginBottom={2}>
                <ul style={{ lineHeight: 1.8 }}>
                  <li>Você está sem conexão com a internet</li>
                  <li>Uma nova versão do sistema foi publicada</li>
                  <li>O cache do navegador está corrompido</li>
                  <li>A conexão foi interrompida durante o carregamento</li>
                </ul>
              </Box>

              <Typography variant='body2' color='textSecondary'>
                <strong>Recomendação:</strong> Clique em "Recarregar Aplicação"
                para atualizar o sistema. Se o problema persistir, limpe o cache
                do navegador (Ctrl+Shift+Del).
              </Typography>
            </Box>
            <Box display='flex' gap={2} flexWrap='wrap' justifyContent='center'>
              <Button
                variant='contained'
                color='primary'
                onClick={this.handleReload}
                startIcon={<Refresh />}
                size='large'
              >
                Recarregar Aplicação
              </Button>

              <Button
                variant='outlined'
                onClick={this.handleGoHome}
                startIcon={<Home />}
                size='large'
              >
                Ir para Início
              </Button>
            </Box>
            {/* Detalhes técnicos (dev mode) */}
            {process.env.NODE_ENV === 'development' && (
              <Box
                marginTop={4}
                padding={2}
                bgcolor='#f5f5f5'
                borderRadius='1px'
                width='100%'
                maxWidth={700}
              >
                <Typography
                  variant='caption'
                  component='div'
                  style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                >
                  <strong>Erro técnico:</strong>
                  <br />
                  {this.state.error?.message}
                  {this.state.error?.stack && (
                    <>
                      <br />
                      <br />
                      <strong>Stack:</strong>
                      <br />
                      {this.state.error.stack}
                    </>
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        )
      }

      // Erro genérico (não relacionado a chunk)
      return (
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          minHeight='50vh'
          padding={4}
          style={{ textAlign: 'center' }}
        >
          <Typography variant='h5' gutterBottom color='error'>
            Erro Inesperado
          </Typography>
          <Box maxWidth={500} marginBottom={3}>
            <Typography variant='body1' color='textSecondary' paragraph>
              Ocorreu um erro inesperado na aplicação.
            </Typography>

            <Typography
              variant='body2'
              color='textSecondary'
              style={{ fontFamily: 'monospace' }}
            >
              {this.state.error?.message || 'Erro desconhecido'}
            </Typography>
          </Box>
          <Box display='flex' gap={2} flexWrap='wrap' justifyContent='center'>
            <Button
              variant='contained'
              color='primary'
              onClick={this.handleRetry}
              size='large'
            >
              Tentar Novamente
            </Button>

            <Button
              variant='outlined'
              onClick={this.handleGoHome}
              startIcon={<Home />}
              size='large'
            >
              Ir para Início
            </Button>
          </Box>
          {/* Detalhes técnicos (sempre mostrar para erros genéricos) */}
          {this.state.errorInfo && (
            <Box
              marginTop={4}
              padding={2}
              bgcolor='#f5f5f5'
              borderRadius='1px'
              width='100%'
              maxWidth={700}
            >
              <Typography
                variant='caption'
                component='div'
                style={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.7rem',
                }}
              >
                <strong>Component Stack:</strong>
                <br />
                {this.state.errorInfo.componentStack}
              </Typography>
            </Box>
          )}
        </Box>
      )
    }

    // Sem erro, renderizar children normalmente
    return this.props.children
  }
}

export default LazyErrorBoundary
